/**
 * The rules of legacy `View/js/cart-apptpref.js` (CAJ) and `View/inc/cart-apptpref.asp` (CAA), as
 * pure functions. No React, no I/O, no host: the picker and every host call the same functions, so
 * "which days are open", "what is picked" and "what text is saved" each have exactly one answer.
 *
 * ★ DATES ARE `YYYY-MM-DD` STRINGS AND ALL ARITHMETIC IS UTC. Nothing here calls `new Date(text)`:
 * that reads a calendar date in the visitor's own zone and can land on the day before. Legacy's own
 * `m/d/yyyy` day key survives in exactly one place — the saved text (`formatPreferenceText`).
 *
 * Deviations from legacy, each deliberate:
 * - the fourth ordinal is "Fourth"; legacy spelled it "Forth" (CAJ:59). Display only — the ordinal
 *   is never part of the saved text;
 * - a day before today is never open. Legacy's lead-time test `os > tos + (daysout - 1)` (CAJ:49)
 *   lets a NEGATIVE `daysout` open days in the past;
 * - `daysOfWeek` is read as a set. Legacy SUMS a bit per entry (CAJ:175), so a design that lists a
 *   weekday twice carries into the next weekday's bit;
 * - "I need something further out" is offered only while `weeks < weeksMax`. Legacy shows it from
 *   the start whatever the numbers are and hides it after a click (CAJ:225-229), so a design whose
 *   `weeks` already equals `weeksmax` got one week more than its maximum;
 * - `requiredSelections`: at least one pick, and never more than can be picked. Legacy compares
 *   against `minSelections` alone (CAJ:197), so `minsel` 0 let an empty preference through and a
 *   `minsel` above `maxsel` could never be satisfied.
 */

/** One preference: a day and the index of a slot name. Legacy `{ day: 'm/d/yyyy', slot }` (CAJ:142). */
export interface PreferencePick {
    /** `YYYY-MM-DD`. */
    date: string;
    /** Index into the slot names. */
    slot: number;
}

/** Which days may be picked from. */
export interface PreferenceDayRules {
    /** "Today" as `YYYY-MM-DD` — the host's answer (legacy `hd-today`, CAA:33). */
    today: string;
    /** Legacy `daysout` (CAA:47): 0 or 1 opens tomorrow, 2 the day after, and so on. */
    daysOut: number;
    /** Legacy `daysofweek` (CAJ:7, CAA:54): 1 = Sunday … 7 = Saturday. */
    daysOfWeek: readonly number[];
    /** Legacy `unavailable` (CAA:55, CAJ:16-19): closed dates, each `YYYY-MM-DD`. */
    unavailable: readonly string[];
    /**
     * CART ONLY — NOT USED BY THE RESCHEDULE HOST. Legacy `options.daysoff` narrowed to
     * `c.team.membersOnSite` (CAJ:21-37): one list of `YYYY-MM-DD` days off per member on site. A
     * day closes only when EVERY one of them is off. `Dashboard/reschedule.asp` never supplies
     * `membersOnSite`, so legacy does not apply this there either.
     */
    daysOff?: readonly (readonly string[])[];
    /**
     * CART ONLY — NOT USED BY THE RESCHEDULE HOST. The weekdays that are OPEN, as legacy's computed
     * `wm` (CAJ:176-191): bit 0 = Sunday … bit 6 = Saturday, 127 = every day. Legacy builds it by
     * OR-ing `127 - weekmask` over the chosen members' service areas — note that is the inverse of
     * `cart-team`'s `TeamMember.weekMask`, which names the weekdays an area CLOSES. Absent = 127,
     * which is also what the reschedule page gets in legacy.
     */
    weekMask?: number;
}

/** Everything the module is configured with — the design's `cart-apptpref.*` variables. */
export interface PreferenceSettings extends PreferenceDayRules {
    /**
     * Slot names in order, `slot1`…`slotN` (CAA:8-11), exactly as the design stores them. A name
     * may be empty, and may hold markup or entities: it is SHOWN as plain text and SAVED untouched.
     */
    slots: readonly string[];
    /** Legacy `weeks` (CAA:48): the grid draws `weeks + 1` rows (CAJ:39). */
    weeks: number;
    /** Legacy `weeksMax` (CAJ:103, CAA:53). */
    weeksMax: number;
    /** Legacy `minSelections` (CAJ:100, CAA:51). */
    minSelections: number;
    /** Legacy `maxSelections` (CAJ:99, CAA:52). */
    maxSelections: number;
}

export type PreferenceCellState = 'past' | 'today' | 'open' | 'closed';

/** One day of the grid — a legacy `<td>` (CAJ:45-65). */
export interface PreferenceCell {
    /** `YYYY-MM-DD`. */
    date: string;
    /** Day of the month, 1-31. */
    day: number;
    /** 0 = January. */
    month: number;
    year: number;
    /** 0 = Sunday. */
    weekday: number;
    /** Legacy prints the month's abbreviation on the first cell and on every 1st (CAJ:45). */
    showsMonth: boolean;
    state: PreferenceCellState;
}

/** Legacy defaults (CAJ:97-104, as overridden by CAA:47-55 when the design sets nothing). */
export const DEFAULT_MIN_SELECTIONS = 3;
export const DEFAULT_MAX_SELECTIONS = 3;
export const DEFAULT_WEEKS_MAX = 4;
export const ALL_DAYS_OF_WEEK: readonly number[] = [1, 2, 3, 4, 5, 6, 7];
/** Legacy has ten ordinals (CAJ:59); an eleventh pick would have no name. */
export const MAX_SELECTIONS_LIMIT = 10;
const MAX_WEEKS_LIMIT = 52;

/** Legacy's header row, word for word (CAJ:11). */
export const WEEKDAY_HEADINGS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thur', 'Fri', 'Sat'] as const;
/** Legacy's month abbreviations (CAJ:45). */
export const MONTH_ABBREVIATIONS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
] as const;
/** Legacy's ordinals (CAJ:59), with "Forth" corrected. */
export const PREFERENCE_ORDINALS = [
    'First',
    'Second',
    'Third',
    'Fourth',
    'Fifth',
    'Sixth',
    'Seventh',
    'Eighth',
    'Ninth',
    'Tenth'
] as const;

const DAY_MS = 86_400_000;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A calendar date as a UTC timestamp, so day arithmetic never crosses a DST change. */
function toUtc(date: string): number {
    const match = ISO_DATE.exec(date);
    if (!match) return Number.NaN;
    return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

const pad = (value: number) => String(value).padStart(2, '0');

function fromUtc(time: number): string {
    const date = new Date(time);
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}

/** True for a real calendar date written `YYYY-MM-DD`. */
export function isIsoDate(value: unknown): value is string {
    if (typeof value !== 'string') return false;
    const time = toUtc(value);
    return Number.isFinite(time) && fromUtc(time) === value;
}

function addDays(date: string, days: number): string {
    return fromUtc(toUtc(date) + days * DAY_MS);
}

/** Whole days from `from` to `to`; negative when `to` is earlier. */
function daysBetween(from: string, to: string): number {
    return Math.round((toUtc(to) - toUtc(from)) / DAY_MS);
}

/** 0 = Sunday. */
function weekdayOf(date: string): number {
    return new Date(toUtc(date)).getUTCDay();
}

/** Legacy's day key (CAJ:50): `m/d/yyyy`, no zero padding. `2026-10-09` → `10/9/2026`. */
export function toLegacyDay(date: string): string {
    const match = ISO_DATE.exec(date);
    if (!match) return date;
    return `${Number(match[2])}/${Number(match[3])}/${Number(match[1])}`;
}

function wholeNumber(value: unknown, fallback: number, max: number): number {
    const number = typeof value === 'number' ? Math.trunc(value) : Number.NaN;
    if (!Number.isFinite(number)) return fallback;
    return Math.min(Math.max(number, 0), max);
}

/**
 * Settings as the module uses them, from whatever a host was given: every missing value takes
 * legacy's default (CAA:47-55 — `weeks` and `daysout` are `Null2Zero`, the selections 3 and 3,
 * `weeksMax` 4, every weekday, nothing unavailable, and NO slots, because the design's list
 * always replaces the script's own, CAA:49), and every number is held to a sane range.
 */
export function normalizePreferenceSettings(
    input: Partial<PreferenceSettings> & { today: string }
): PreferenceSettings {
    const daysOfWeek = Array.isArray(input.daysOfWeek)
        ? input.daysOfWeek.filter(day => Number.isInteger(day) && day >= 1 && day <= 7)
        : ALL_DAYS_OF_WEEK;
    return {
        today: input.today,
        slots: Array.isArray(input.slots) ? input.slots.map(name => String(name ?? '')) : [],
        weeks: wholeNumber(input.weeks, 0, MAX_WEEKS_LIMIT),
        weeksMax: wholeNumber(input.weeksMax, DEFAULT_WEEKS_MAX, MAX_WEEKS_LIMIT),
        daysOut: wholeNumber(input.daysOut, 0, 366),
        minSelections: wholeNumber(
            input.minSelections,
            DEFAULT_MIN_SELECTIONS,
            MAX_SELECTIONS_LIMIT
        ),
        maxSelections: wholeNumber(
            input.maxSelections,
            DEFAULT_MAX_SELECTIONS,
            MAX_SELECTIONS_LIMIT
        ),
        daysOfWeek,
        unavailable: Array.isArray(input.unavailable) ? input.unavailable.filter(isIsoDate) : [],
        ...(input.daysOff !== undefined ? { daysOff: input.daysOff } : {}),
        ...(input.weekMask !== undefined ? { weekMask: input.weekMask } : {})
    };
}

/**
 * Whether the grid can be used at all: there is a "today" to count from, at least one slot to
 * pick and room for at least one pick. A host shows something else when this is false — legacy
 * would draw a grid nobody can pick from, and its Save could then never enable.
 */
export function isPreferenceUsable(
    settings: Pick<PreferenceSettings, 'today' | 'slots' | 'maxSelections'>
): boolean {
    return isIsoDate(settings.today) && settings.slots.length > 0 && settings.maxSelections >= 1;
}

/**
 * How many picks a host needs before it may continue — legacy `preferences.length <
 * minSelections` (CAJ:197, `reschedule.asp` :360), held between 1 and `maxSelections`.
 */
export function requiredSelections(
    settings: Pick<PreferenceSettings, 'minSelections' | 'maxSelections'>
): number {
    return Math.max(1, Math.min(settings.minSelections, settings.maxSelections));
}

/**
 * The cart's days off (CAJ:21-37): the days EVERY member on site has off. With nobody on site
 * there are none.
 */
export function sharedDaysOff(daysOff: readonly (readonly string[])[] | undefined): string[] {
    if (!daysOff || daysOff.length === 0) return [];
    const [first, ...rest] = daysOff;
    return first.filter(day => rest.every(list => list.includes(day)));
}

/**
 * Whether slots are offered on `date` (CAJ:46-51): it is after today and at least `daysOut` days
 * away, its weekday is allowed — by the design and, on the cart, by the service areas — and it is
 * neither unavailable nor a day off shared by everyone on site.
 */
export function isDayOpen(date: string, rules: PreferenceDayRules): boolean {
    const offset = daysBetween(rules.today, date);
    if (!Number.isFinite(offset)) return false;
    // `os == tos` is "Today" and never has slots (CAJ:46); then `os > tos + (daysout - 1)` (CAJ:49).
    if (offset < Math.max(rules.daysOut, 1)) return false;
    const weekday = weekdayOf(date);
    if (!rules.daysOfWeek.includes(weekday + 1)) return false;
    // `cartApptPref.dow = (dow & wm)` (CAJ:192), tested per column as `dow & 2^b` (CAJ:51).
    if (rules.weekMask !== undefined && (rules.weekMask & (1 << weekday)) === 0) return false;
    if (rules.unavailable.includes(date)) return false;
    return !sharedDaysOff(rules.daysOff).includes(date);
}

/**
 * The grid (CAJ:38-68): `weeks + 1` rows of seven days, the first row starting on the Sunday of
 * today's week.
 */
export function buildPreferenceGrid(
    rules: PreferenceDayRules & { weeks: number }
): PreferenceCell[][] {
    if (!isIsoDate(rules.today)) return [];
    const todayWeekday = weekdayOf(rules.today);
    const first = addDays(rules.today, -todayWeekday);
    const rows: PreferenceCell[][] = [];
    for (let week = 0; week <= Math.max(0, Math.trunc(rules.weeks)); week++) {
        const row: PreferenceCell[] = [];
        for (let weekday = 0; weekday < 7; weekday++) {
            const offset = week * 7 + weekday;
            const date = addDays(first, offset);
            const [year, month, day] = date.split('-').map(Number);
            let state: PreferenceCellState;
            if (offset < todayWeekday) state = 'past';
            else if (offset === todayWeekday) state = 'today';
            else if (isDayOpen(date, rules)) state = 'open';
            else state = 'closed';
            row.push({
                date,
                day,
                month: month - 1,
                year,
                weekday,
                showsMonth: offset === 0 || day === 1,
                state
            });
        }
        rows.push(row);
    }
    return rows;
}

/** Where `(date, slot)` sits in the picks — its ordinal — or -1 when it is not picked (CAJ:53-54). */
export function pickIndex(picks: readonly PreferencePick[], date: string, slot: number): number {
    return picks.findIndex(pick => pick.date === date && pick.slot === slot);
}

/**
 * Legacy `slotClick` (CAJ:139-146): the pick goes on the end while there is room. At
 * `maxSelections` nothing changes (the same array comes back). A slot that is already picked is
 * not added twice — legacy draws no button for it, so it could not be clicked again.
 */
export function addPick(
    picks: readonly PreferencePick[],
    pick: PreferencePick,
    maxSelections: number
): readonly PreferencePick[] {
    if (picks.length >= maxSelections) return picks;
    if (pickIndex(picks, pick.date, pick.slot) !== -1) return picks;
    return [...picks, { date: pick.date, slot: pick.slot }];
}

/** Legacy `slotRemove` (CAJ:158-161): the pick goes and the later ones move up one ordinal. */
export function removePick(
    picks: readonly PreferencePick[],
    index: number
): readonly PreferencePick[] {
    if (!Number.isInteger(index) || index < 0 || index >= picks.length) return picks;
    return picks.filter((_, position) => position !== index);
}

/**
 * Legacy's redraw (CAJ:38, :60, :69): a pick is kept only if its slot is drawn — the day is an
 * open cell of the grid and the slot exists. The order of the rest is unchanged. Returns the SAME
 * array when nothing was dropped, so a caller can compare by identity.
 */
export function prunePicks(
    picks: readonly PreferencePick[],
    grid: readonly (readonly PreferenceCell[])[],
    slotCount: number
): readonly PreferencePick[] {
    const open = new Set<string>();
    for (const row of grid) {
        for (const cell of row) {
            if (cell.state === 'open') open.add(cell.date);
        }
    }
    const kept = picks.filter(
        pick =>
            open.has(pick.date) &&
            Number.isInteger(pick.slot) &&
            pick.slot >= 0 &&
            pick.slot < slotCount
    );
    return kept.length === picks.length ? picks : kept;
}

/**
 * ★ THE SAVED TEXT — BYTE FOR BYTE LEGACY'S (CAJ:201-205). Picks in pick order, each
 * `<m/d/yyyy> - <slot name>`, joined with `", "`; no zero padding, nothing trimmed, nothing
 * escaped: `10/9/2026 - Morning, 10/12/2026 - Afternoon`. This is what `sApptPref` carries and
 * what staff read on the order. A slot name with a comma in it, or an empty one, goes in exactly
 * as it is.
 */
export function formatPreferenceText(
    picks: readonly PreferencePick[],
    slots: readonly string[]
): string {
    return picks.map(pick => `${toLegacyDay(pick.date)} - ${slots[pick.slot] ?? ''}`).join(', ');
}

/** Whether "I need something further out" is offered (CAJ:225-229). See the deviation above. */
export function canAddWeek(weeks: number, weeksMax: number): boolean {
    return weeks < weeksMax;
}

/** "First", "Second", … for the pick at `index` (CAJ:59). Empty beyond the tenth. */
export function ordinal(index: number): string {
    return PREFERENCE_ORDINALS[index] ?? '';
}
